import React, {useCallback, useEffect, useState} from "react";
import {useAuth} from "../../context/user";
import {usePermissions} from "../../context/modulePermissions";
import {Layout} from "../../pages/siren/Layout";
import {SirenSMSConfig, getSirenSMSConfigExact} from "./hooks/useGetSirenSMSConfig";
import {useUpsertSirenSMSConfig} from "./hooks/useUpsertSirenSMSConfig";
import {useDeleteSirenSMSConfig} from "./hooks/useDeleteSirenSMSConfig";
import {ScopeSplitSelector, ScopeSelection} from "./components/ScopeSplitSelector";
import {IconButton} from "./import/inputs";
import {useScopeQueryParams} from "./hooks/useScopeQueryParams";
import {useSiren} from "../../pages/siren/SirenContext";
import BasicText, {BasicTextArea} from "../../components/form-v2/basic-text";
import {CountrySelectorSimple} from "../../components/paginated-selector/country-selector-simple";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";

type SMSConfigPanelProps = {
    scope: ScopeSelection;
};

const SMSConfigPanel: React.FC<SMSConfigPanelProps> = ({scope}) => {
    const [record, setRecord] = useState<SirenSMSConfig | null>(null);
    const [loading, setLoading] = useState(true);
    const [saved, setSaved] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    const [portalURL, setPortalURL] = useState("");
    const [introMessage, setIntroMessage] = useState("");
    const [exemptions, setExemptions] = useState<string[]>([]);

    const {save, saving, error: saveError} = useUpsertSirenSMSConfig();
    const {deleteConfig, deleting} = useDeleteSirenSMSConfig();
    const {hasFullAccess} = usePermissions();

    useEffect(() => {
        setLoading(true);
        setSaved(false);
        const params: Record<string, string> = {};
        if (scope.client) params.clientID = scope.client.id;
        if (scope.department) params.departmentID = scope.department.id;
        getSirenSMSConfigExact(params).then(r => {
            setRecord(r);
            setPortalURL(r?.sirenRecipientPortalURL ?? "");
            setIntroMessage(r?.introductoryMessage ?? "");
            setExemptions(r?.smsOptInExemptions ?? []);
        }).finally(() => setLoading(false));
    }, [scope.client?.id, scope.department?.id]);

    const handleSave = useCallback(async () => {
        setSaved(false);
        try {
            const result = await save({
                clientID: scope.client?.id,
                departmentID: scope.department?.id,
                sirenRecipientPortalURL: portalURL,
                introductoryMessage: introMessage,
                smsOptInExemptions: exemptions,
            });
            setRecord(result);
            setSaved(true);
        } catch { /* saveError shown */ }
    }, [scope, portalURL, introMessage, exemptions, save]);

    const handleDelete = useCallback(async () => {
        if (!record) return;
        setDeleteConfirm(false);
        try {
            await deleteConfig(record.id, {clientID: scope.client?.id, departmentID: scope.department?.id});
            setRecord(null);
            setPortalURL("");
            setIntroMessage("");
            setExemptions([]);
        } catch { /* ignore */ }
    }, [record, deleteConfig]);

    if (loading) return <div className="p-4 text-sm text-light-grey">Loading…</div>;

    return (
        <div className={`flex flex-col gap-4 p-4 overflow-y-auto${!hasFullAccess("siren_config") ? " pointer-events-none" : ""}`}>
            <div>
                <p className="font-semibold text-sm">{scope.client ? scope.client.name : "System Default"}</p>
                <p className="text-xs text-light-grey">{scope.client ? (scope.department ? scope.department.name : "Client level") : "Global fallback — no client"}</p>
            </div>

            <div className="bg-white shadow-md rounded-lg p-4 flex flex-col gap-4">
                <BasicText
                    label="Recipient Portal URL"
                    value={portalURL}
                    onChange={setPortalURL}
                    placeholder="https://portal.example.com"
                />
                <BasicTextArea
                    label="Introductory Message"
                    value={introMessage}
                    onChange={setIntroMessage}
                    placeholder="Reply STOP to unsubscribe."
                />

                <div className="flex flex-col gap-2">
                    <p className="text-sm font-medium">SMS Opt-in Exemptions</p>
                    <p className="text-xs text-light-grey">Countries exempt from SMS opt-in requirements.</p>
                    <div className="pointer-events-auto">
                        <CountrySelectorSimple selectedIDs={exemptions} setSelectedIDs={hasFullAccess("siren_config") ? setExemptions : () => {}}/>
                    </div>
                </div>

                {saveError && (
                    <div className="bg-red text-white text-sm text-center py-2 px-4 rounded-md">{saveError}</div>
                )}
                {saved && !saving && (
                    <div className="bg-green text-white text-sm text-center py-2 px-4 rounded-md">Saved</div>
                )}

                {hasFullAccess("siren_config") && (
                    <div className="flex justify-end gap-2">
                        {record && (
                            <IconButton text="Delete" icon={<DeleteIcon style={{fontSize: 16}}/>} color="red" className="w-28" onClick={() => setDeleteConfirm(true)} disabled={saving || deleting}/>
                        )}
                        <IconButton
                            text={record ? "Update" : "Create"}
                            icon={record ? <SaveIcon style={{fontSize: 16}}/> : <AddIcon style={{fontSize: 16}}/>}
                            className="w-28"
                            onClick={handleSave}
                            loading={saving}
                            disabled={saving || deleting}
                        />
                    </div>
                )}
            </div>

            {deleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col gap-4 max-w-sm w-full">
                        <h3 className="font-bold">Delete SMS Config</h3>
                        <p className="text-sm">Delete SMS config for <span className="font-semibold">{scope.client ? scope.client.name : "System Default"}</span>{scope.department ? <> / <span className="font-semibold">{scope.department.name}</span></> : ""}?</p>
                        <div className="flex gap-2 justify-end">
                            <button className="px-4 py-2 text-sm rounded-md border border-light-grey hover:bg-light-light-grey" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                            <button className="px-4 py-2 text-sm rounded-md bg-red text-white hover:bg-opacity-80 disabled:opacity-50" disabled={deleting} onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SirenSMSConfigPage: React.FC = () => {
    const {clientID} = useSiren();
    const {isSuperAdmin, isDepartmentAdmin} = useAuth();
    const {scope: selectedScope, drillClientID, drillClientName, deptPage, onSelect, onDrill, onDeptPageChange, onClear} = useScopeQueryParams({defaultClientID: isSuperAdmin ? null : clientID});

    return (
        <Layout menuCurrent="config">
            <div className="flex gap-4 h-full">
                <div className="w-[460px] shrink-0">
                    <ScopeSplitSelector
                        selectedScope={selectedScope}
                        onSelect={onSelect}
                        onClear={onClear}
                        onDrill={onDrill}
                        initialClientID={drillClientID}
                        initialClientName={drillClientName}
                        deptPage={deptPage}
                        onDeptPageChange={onDeptPageChange}
                        hydrate="smsConfigExistence"
                        showSystemDefault={isSuperAdmin}
                        showClientLevel={!isDepartmentAdmin}
                    />
                </div>
                <div className="flex-1 min-w-0 flex flex-col">
                    {selectedScope ? (
                        <SMSConfigPanel
                            key={`${selectedScope.client?.id ?? "system"}-${selectedScope.department?.id ?? "client"}`}
                            scope={selectedScope}
                        />
                    ) : (
                        <div className="p-5 text-sm text-light-grey">
                            Select a client, then a scope to configure.
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default SirenSMSConfigPage;
